import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { initials } from "@/lib/formatters";
import { updateProject } from "@/api/projects";
import { getEnrichmentRates, listEnrichmentMembers } from "@/api/enrichment";
import { enrichmentCurrencySymbol, formatEnrichmentMoney } from "@/lib/enrichmentMoney";
import { useProjectStore } from "@/stores/project.store";
import type { EnrichmentMember, Project } from "@/api/types";

interface EnrichmentRatesSectionProps {
  project: Project;
}

type RateField = "name" | "linkedin" | "phone" | "email";

interface FieldRates {
  name: number;
  linkedin: number;
  phone: number;
  email: number;
}

interface RatesConfig {
  default: FieldRates;
  by_user: Record<string, FieldRates>;
}

/** Backstop defaults used when neither the project config nor the
 *  M&A seed have shipped explicit rates yet. Per-lead in USD. */
const FALLBACK_DEFAULT: FieldRates = {
  name: 0.02,
  linkedin: 0.03,
  phone: 0.02,
  email: 0.06,
};

const FIELDS: Array<{
  key: RateField;
  icon: string;
  label: string;
  description: string;
}> = [
  {
    key: "name",
    icon: "👤",
    label: "Owner's name",
    description: "Correcting or finding the executive's name",
  },
  {
    key: "linkedin",
    icon: "🔗",
    label: "LinkedIn",
    description: "Finding the LinkedIn profile URL",
  },
  {
    key: "phone",
    icon: "📞",
    label: "Phone number",
    description: "Finding the phone number via Kaspr / Lusha",
  },
  {
    key: "email",
    icon: "✉️",
    label: "Email",
    description: "Finding the email via Kaspr / Lusha",
  },
];

/** Pull a clean `RatesConfig` out of `project.config.enrichment_rates`.
 *  Falls back to the seed defaults when the project hasn't been
 *  configured yet — the operator can still edit + save and the
 *  backend will accept the patch on first write. */
function readRatesConfig(project: Project): RatesConfig {
  const raw = (project.config?.enrichment_rates ?? {}) as Record<
    string,
    unknown
  >;
  const rawDefault = (raw.default ?? {}) as Record<string, unknown>;
  const rawByUser = (raw.by_user ?? {}) as Record<string, unknown>;
  return {
    default: {
      name: toNum(rawDefault.name, FALLBACK_DEFAULT.name),
      linkedin: toNum(rawDefault.linkedin, FALLBACK_DEFAULT.linkedin),
      phone: toNum(rawDefault.phone, FALLBACK_DEFAULT.phone),
      email: toNum(rawDefault.email, FALLBACK_DEFAULT.email),
    },
    by_user: Object.fromEntries(
      Object.entries(rawByUser).map(([k, v]) => {
        const o = (v ?? {}) as Record<string, unknown>;
        return [
          k,
          {
            name: toNum(o.name, 0),
            linkedin: toNum(o.linkedin, 0),
            phone: toNum(o.phone, 0),
            email: toNum(o.email, 0),
          },
        ];
      }),
    ),
  };
}

function toNum(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return fallback;
}

function fullPerLead(r: FieldRates): number {
  return r.name + r.linkedin + r.phone + r.email;
}

/** Per-project enrichment rates editor. Writes back to
 *  `project.config.enrichment_rates` via PATCH /projects/:slug; the
 *  Stats tab reads the same blob (via the backend's enrichment-stats
 *  endpoint) so a rate change here immediately affects displayed
 *  earnings without a frontend round-trip. */
export function EnrichmentRatesSection({
  project,
}: EnrichmentRatesSectionProps) {
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  const [rates, setRates] = useState<RatesConfig>(() => readRatesConfig(project));
  const initialRates = useMemo(() => readRatesConfig(project), [project]);
  const [members, setMembers] = useState<EnrichmentMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currency, setCurrency] = useState<string | null>(null);
  const currencySymbol = enrichmentCurrencySymbol(currency);

  // Re-sync local state when the project prop changes (e.g. after a
  // save updates the store).
  useEffect(() => {
    setRates(initialRates);
  }, [initialRates]);

  useEffect(() => {
    let cancelled = false;
    setMembersLoading(true);
    setCurrency(null);
    getEnrichmentRates(project.slug)
      .then((response) => {
        if (!cancelled) setCurrency(response.currency);
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load the rate currency");
      });
    listEnrichmentMembers(project.slug)
      .then((m) => {
        if (cancelled) return;
        setMembers(m);
      })
      .catch(() => {
        if (cancelled) return;
        setMembers([]);
      })
      .finally(() => {
        if (cancelled) return;
        setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug]);

  const hasChanges = useMemo(
    () => JSON.stringify(rates) !== JSON.stringify(initialRates),
    [rates, initialRates],
  );

  function setDefault(field: RateField, value: number) {
    setRates((prev) => ({
      ...prev,
      default: { ...prev.default, [field]: value },
    }));
  }

  function setUserRate(userId: string, field: RateField, value: number) {
    setRates((prev) => ({
      ...prev,
      by_user: {
        ...prev.by_user,
        [userId]: { ...prev.by_user[userId], [field]: value },
      },
    }));
  }

  function toggleOverride(userId: string, enabled: boolean) {
    setRates((prev) => {
      if (enabled) {
        return {
          ...prev,
          by_user: { ...prev.by_user, [userId]: { ...prev.default } },
        };
      }
      const next = { ...prev.by_user };
      delete next[userId];
      return { ...prev, by_user: next };
    });
  }

  async function handleSave() {
    setSubmitting(true);
    try {
      // PATCH /projects/:slug merges `config` shallowly one level
      // deep — sending `{ config: { enrichment_rates: rates }}` only
      // touches the `enrichment_rates` key, leaving the rest of the
      // config untouched.
      const updated = await updateProject(project.slug, {
        config: { enrichment_rates: rates },
      });
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.id === updated.id ? updated : p)),
      );
      toast.success("Enrichment rates saved");
    } catch {
      toast.error("Couldn't save the rates, try again");
    } finally {
      setSubmitting(false);
    }
  }

  const defaultTotal = fullPerLead(rates.default);

  return (
    <div className="space-y-6">
      <section className="surface-plane space-y-4 p-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Default Rates</h3>
          <p className="text-xs text-slate-500">
            Applied to all members unless overridden below.
          </p>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {FIELDS.map((f) => (
            <div
              key={f.key}
              className="rounded-lg border border-slate-900/[0.06] p-3 text-center"
              title={f.description}
            >
              <span className="text-lg">{f.icon}</span>
              <div className="mt-1 text-xs text-slate-500">{f.label}</div>
              <div className="mt-2 flex items-center justify-center gap-1">
                <span className="text-slate-400">{currencySymbol}</span>
                <Input
                  type="number"
                  lang="en-US"
                  step="0.01"
                  min="0"
                  value={rates.default[f.key]}
                  onChange={(e) => setDefault(f.key, toNum(e.target.value, 0))}
                  className="h-8 w-24 text-center font-mono text-sm"
                />
              </div>
              <div className="mt-1 text-[9px] text-slate-400">per lead</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg bg-slate-50 px-4 py-2 text-center text-sm text-slate-600">
          Full enrichment (all fields):{" "}
          <strong className="text-emerald-600">
            {formatEnrichmentMoney(defaultTotal, currency)}
          </strong>{" "}
          per lead
        </div>
      </section>

      <section className="surface-plane space-y-4 p-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">
            Member Overrides
          </h3>
          <p className="text-xs text-slate-500">
            Set custom rates for specific team members.
          </p>
        </div>

        {membersLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-slate-400">No members on this project.</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => {
              const userRates = rates.by_user[m.user_id];
              const hasOverride = !!userRates;
              return (
                <div
                  key={m.user_id}
                  className="rounded-lg border border-slate-200 p-3"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-indigo-100 text-xs font-medium text-indigo-600">
                          {initials(m.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800">
                          {m.full_name}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {m.email}
                          {m.role && (
                            <span className="ml-1 text-slate-300">
                              · {m.role}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={hasOverride}
                      onCheckedChange={(v) =>
                        toggleOverride(m.user_id, v === true)
                      }
                    />
                  </div>

                  {hasOverride && (
                    <div className="grid grid-cols-4 gap-2">
                      {FIELDS.map((f) => (
                        <div key={f.key} className="text-center">
                          <div className="text-[10px] text-slate-400">
                            {f.icon} {f.label}
                          </div>
                          <div className="mt-1 flex items-center justify-center gap-1">
                            <span className="text-xs text-slate-400">{currencySymbol}</span>
                            <Input
                              type="number"
                              lang="en-US"
                              step="0.01"
                              min="0"
                              value={userRates[f.key]}
                              onChange={(e) =>
                                setUserRate(
                                  m.user_id,
                                  f.key,
                                  toNum(e.target.value, 0),
                                )
                              }
                              className="h-7 w-24 text-center font-mono text-xs"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="col-span-4 text-center text-[10px] text-slate-400">
                        Override total: {formatEnrichmentMoney(fullPerLead(userRates), currency)} per
                        lead
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {hasChanges && (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setRates(initialRates)}
            disabled={submitting}
          >
            Discard
          </Button>
          <Button onClick={() => void handleSave()} disabled={submitting || !currency}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Rates
          </Button>
        </div>
      )}
    </div>
  );
}
